import { z } from 'zod'

/**
 * A plateia como evento. docs/05-protocolo-tempo-real.md secao 3.3, ADR 0046.
 *
 * Mora aqui porque tres processos leem as mesmas coisas: a sala (`game-server`) valida e soma, o `api` guarda o
 * retrato do duelo vivo, e o `web2` desenha. Tudo isto e do TRANSPORTE, como a presenca dos assentos: nada entra em
 * `SpectatorView`, e nada chega aos duelistas.
 */

/** As cinco reacoes. Lista fechada: reacao e um id, nunca texto (docs/00: sem chat livre). */
export const SPECTATOR_REACTIONS = ['FIRE', 'CLAP', 'WOW', 'MIND_BLOWN', 'RESPECT'] as const
export type SpectatorReaction = (typeof SPECTATOR_REACTIONS)[number]

/** Uma reacao por espectador a cada intervalo; a que chega antes e descartada (docs/10 secao 4). */
export const SPECTATOR_REACT_INTERVAL_MS = 1_000

/** No maximo um retrato do duelo vivo por duelo a cada intervalo; o ultimo valor sempre sai (ADR 0046). */
export const LIVE_REPORT_INTERVAL_MS = 5_000

/** `SPECTATOR_REACT`, cliente -> servidor, so de espectador. */
export const spectatorReactSchema = z.object({ reaction: z.enum(SPECTATOR_REACTIONS) })
export type SpectatorReact = z.infer<typeof spectatorReactSchema>

/** `SPECTATOR_PREDICT`, cliente -> servidor, so de espectador: o assento em que ele aposta. Um voto, sem troca. */
export const spectatorPredictSchema = z.object({ playerId: z.string().min(1) })
export type SpectatorPredict = z.infer<typeof spectatorPredictSchema>

const reactionCountsSchema = z.object(
  Object.fromEntries(SPECTATOR_REACTIONS.map((id) => [id, z.number().int().nonnegative()])) as Record<
    SpectatorReaction,
    z.ZodNumber
  >,
)
export type SpectatorReactionCounts = Record<SpectatorReaction, number>

/**
 * `CROWD`, servidor -> plateia. `votes` na ordem dos assentos (`SpectatorView.sides`); `reactions` e o total do
 * duelo, `burst` o que chegou desde o ultimo envio, que a tela anima.
 */
export const crowdMessageSchema = z.object({
  count: z.number().int().nonnegative(),
  peak: z.number().int().nonnegative(),
  votes: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
  reactions: reactionCountsSchema,
  burst: reactionCountsSchema,
})
export type CrowdMessage = z.infer<typeof crowdMessageSchema>

/** `MY_PREDICTION`, servidor -> o espectador que votou. */
export const myPredictionMessageSchema = z.object({ playerId: z.string().min(1) })
export type MyPredictionMessage = z.infer<typeof myPredictionMessageSchema>

/** Zero em cada reacao: o ponto de partida do total e do lote. */
export const emptyReactionCounts = (): SpectatorReactionCounts =>
  Object.fromEntries(SPECTATOR_REACTIONS.map((id) => [id, 0])) as SpectatorReactionCounts

/**
 * O retrato do duelo vivo, que a sala reporta ao `api` (`POST /internal/duels/live`) e a lista publica. `lifePoints`
 * na ordem dos assentos. LP e turno sao a coluna "completo" das tres visoes (docs/05 secao 4): nada oculto sai.
 */
export const liveDuelSnapshotSchema = z.object({
  duelId: z.string().min(1),
  turn: z.number().int().nonnegative(),
  lifePoints: z.tuple([z.number().int(), z.number().int()]),
  spectators: z.number().int().nonnegative(),
  /**
   * As pilotadas sentadas na plateia (ADR 0058 decisao 5), ja contadas em `spectators`. INTERNO: nunca sai em
   * `GET /duels/live`, em `CROWD` nem em outro payload publico. Opcional para o retrato de uma sala anterior a plateia
   * pilotada, que nao o manda: ausente e o mesmo que nenhuma.
   */
  pilotSpectators: z.array(z.string().min(1)).optional(),
  /**
   * Os tetos que a sala recebeu na criacao (ADR 0058 decisao 4): `spectatorSlots` de pessoas e `pilotSlots` de
   * pilotadas. INTERNOS, como `pilotSpectators`: o `api` escolhe o duelo pelo teto da sala, e nao pelo painel de agora.
   * Ausentes na sala sem plateia livre ou anterior a eles.
   */
  spectatorSlots: z.number().int().nonnegative().optional(),
  pilotSlots: z.number().int().nonnegative().optional(),
})
export type LiveDuelSnapshot = z.infer<typeof liveDuelSnapshotSchema>

/**
 * A plateia livre na criacao da sala (`POST /internal/rooms`, ADR 0058 decisoes 3 e 4). So a criacao da plateia livre
 * (`withLiveSpectators`) manda: sem `freeAudience`, a sala recusa a pilotada (`NO_FREE_AUDIENCE`). `pilotSlots` e o
 * teto de pilotadas, `floor(spectatorSlots * pilotShare)`, calculado pelo `api` na criacao.
 */
export const roomAudienceSchema = z.object({
  freeAudience: z.boolean().optional(),
  pilotSlots: z.number().int().nonnegative().optional(),
})
export type RoomAudience = z.infer<typeof roomAudienceSchema>

export const AUDIENCE_ACTIONS = ['JOIN', 'LEAVE'] as const
export type AudienceAction = (typeof AUDIENCE_ACTIONS)[number]

/**
 * As faixas dos parametros da plateia no JOIN, as mesmas do painel (backlog 155#API-002, `PilotAudienceConfig`). Acima
 * delas o numero deixa de ser ajuste e vira engano.
 */
export const AUDIENCE_SETTINGS_LIMITS = {
  reactivityScale: 2,
  reactionDelayMs: 15_000,
  maxReactionsPerDuelPerPilot: 50,
  crowdWindowMs: { min: 1_000, max: 60_000 },
} as const

const msFrom = (floor: number, ceiling?: number) => {
  const atLeast = z.number().int().min(floor)
  return ceiling === undefined ? atLeast : atLeast.max(ceiling)
}

const periodFits = (field: string) => ({ message: `${field}MinMs nao pode passar de ${field}MaxMs`, path: [field] })

/**
 * Como a plateia pilotada reage e vota na sala (backlog 155, "As reacoes" e "O palpite"). Os nomes sao os campos de
 * `PilotAudienceConfig`. O `api` manda o bloco em cada JOIN, lido sem cache, e a sala aplica o ultimo recebido a sala
 * inteira (ADR 0058 decisao 7).
 */
export const audienceSettingsSchema = z
  .object({
    reactivityScale: z.number().min(0).max(AUDIENCE_SETTINGS_LIMITS.reactivityScale),
    reactionDelayMinMs: msFrom(0, AUDIENCE_SETTINGS_LIMITS.reactionDelayMs),
    reactionDelayMaxMs: msFrom(0, AUDIENCE_SETTINGS_LIMITS.reactionDelayMs),
    // Abaixo do intervalo da sala, a reacao da pilotada seria descartada pelo `react()`.
    reactCooldownMinMs: msFrom(SPECTATOR_REACT_INTERVAL_MS),
    reactCooldownMaxMs: msFrom(SPECTATOR_REACT_INTERVAL_MS),
    maxReactionsPerDuelPerPilot: z.number().int().min(0).max(AUDIENCE_SETTINGS_LIMITS.maxReactionsPerDuelPerPilot),
    maxPilotReactionsPerWindow: z.number().int().min(0),
    crowdWindowMs: msFrom(AUDIENCE_SETTINGS_LIMITS.crowdWindowMs.min, AUDIENCE_SETTINGS_LIMITS.crowdWindowMs.max),
    predictEnabled: z.boolean(),
    predictShare: z.number().min(0).max(1),
    predictDelayMinMs: msFrom(0),
    predictDelayMaxMs: msFrom(0),
  })
  .refine((value) => value.reactionDelayMinMs <= value.reactionDelayMaxMs, periodFits('reactionDelay'))
  .refine((value) => value.reactCooldownMinMs <= value.reactCooldownMaxMs, periodFits('reactCooldown'))
  .refine((value) => value.predictDelayMinMs <= value.predictDelayMaxMs, periodFits('predictDelay'))
export type AudienceSettings = z.infer<typeof audienceSettingsSchema>

/**
 * `POST /internal/rooms/:roomId/audience`, `api` -> `game-server` (docs/05 secao 3.3, ADR 0058 decisao 2). O JOIN leva
 * `dwellMs`, e a sala agenda a propria saida; o LEAVE serve so para a preempcao.
 *
 * O JOIN leva tambem `settings`, os parametros de reacao e de palpite. Opcional por compatibilidade: sem ele, a sala
 * segue com o ultimo recebido, ou com os padroes se nunca recebeu. O LEAVE nao leva.
 */
export const audienceInstructionSchema = z
  .object({
    duelId: z.string().min(1),
    pilotId: z.string().min(1),
    action: z.enum(AUDIENCE_ACTIONS),
    dwellMs: z.number().int().positive().optional(),
    settings: audienceSettingsSchema.optional(),
  })
  // Sem `dwellMs`, a sala nao saberia quando soltar, e um LEAVE perdido deixaria o virtual orfao.
  .refine((value) => value.action !== 'JOIN' || value.dwellMs !== undefined, { message: 'JOIN exige dwellMs' })
  .refine((value) => value.action !== 'LEAVE' || value.settings === undefined, { message: 'LEAVE nao leva settings' })
export type AudienceInstruction = z.infer<typeof audienceInstructionSchema>

/**
 * As recusas da rota de audiencia. `NOT_ON_THIS_NODE` (404) e distinto de `DUEL_FINISHED` (409), para que a sala em
 * outro processo nao seja lida como duelo acabado (ADR 0058 decisao 3).
 */
export const AUDIENCE_REFUSALS = ['OWN_DUEL', 'NO_FREE_AUDIENCE', 'FULL', 'DUEL_FINISHED', 'NOT_ON_THIS_NODE'] as const
export type AudienceRefusal = (typeof AUDIENCE_REFUSALS)[number]

export const audienceRefusalSchema = z.object({ code: z.enum(AUDIENCE_REFUSALS) })
export type AudienceRefusalBody = z.infer<typeof audienceRefusalSchema>

/**
 * Uma linha de `GET /duels/live` (docs/18 secao 40.2). `names` e `characters` na ordem dos assentos, `null` para
 * quem escondeu o nome (e por isso o rosto, docs/05 secao 3.0.3). `lifePoints`, `turn` e `spectators` sao `null`
 * enquanto a sala nao mandou o retrato.
 */
export const liveDuelListItemSchema = z.object({
  duelId: z.string().min(1),
  mode: z.string().min(1),
  startedAt: z.string().min(1),
  names: z.tuple([z.string().nullable(), z.string().nullable()]),
  characters: z.tuple([z.string().nullable(), z.string().nullable()]).default([null, null]),
  lifePoints: z.tuple([z.number().int(), z.number().int()]).nullable().default(null),
  turn: z.number().int().nonnegative().nullable().default(null),
  spectators: z.number().int().nonnegative().nullable().default(null),
})
export type LiveDuelListItem = z.infer<typeof liveDuelListItemSchema>
